import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent4: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Calculate TAM for beachhead market</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Calculate TAM for beachhead market
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                    <p className="fs-7">In this step you will use the demographics from Step 3 to quantify the size of your beachhead market. Once this is known, you can then determine if it’s the appropriate size.</p>

                    <p className="fs-7">If the market is too small, there is not enough opportunity to support a business and you have to rethink whether this is an appropriate market. More likely, though, the market segment will be too big, which will help you see further segmentation is needed to identify a more appropriately sized beachhead market. Then again, if you find the market size is appropriate for your beachhead market, you move forward.</p>

                    <p className="fs-7">Your chosen beachhead market's TAM size is the anual revenue your business would earn if it had 100% market share for that market. You will modify this as time goes on, but it is wise to be thinking about this point and develop at least a rough market size to know you are heading in the right general direction.</p>
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Bottom-up analysis
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                    <p className="fs-7">First, find out how many customers are out there—often called “counting noses” because you have to get very specific about customers. This is done with primary market research techniques combined with some secondary market research, but dominated by the former.</p>

                    <p className="fs-7">Talk to as many people as possible in your end-user profile group. Find out from them how you can estimate the size.</p>
                    <p className="fs-7">If it is a B2B business, find out how many there are in a few representative companies.
                    Then, determine the average density of your end-users in companies in your targeted area. This ratio (end users per 1,000 employees, or end users per $1M in revenue, or end users per product produced, or whatever is most appropriate in your situation) is an important metric that we can then extrapolate to the other companies, and build up your estimate in that way.</p>
                    <p className="fs-7">Collect and analyze source data (customer lists, trade associations, other sources of customer information) for things like lists of specific companies in the sector you are targeting. In the B2B example above, you can then estimate how many end users for each company name on the list. Company can be replaced with hospital, high school, region, fan club, or whatever is the most appropriate organizational unit for your project.</p>
                    <p className="fs-7">Make an estimate of how many potential users there are in the market. This is the first number that you need to know.</p>
                    <p className="fs-7">Next, come up with a valid range of how much revenue could be generated from an average single-target end user that fits your profile in the previous step. This is the amount of money they would spend in one year. If they buy your product which costs $100 once every five years, then the annual revenue for this is $100/5 years = $20/year. The more primary market research you can get to build the ratio for this number as well as the annual revenue per user (which can also be a range), the better.</p>
                    <p className="fs-7">Now calculate the bottom-up value of your TAM (number of customers × revenue per customer).
                    Estimate the CAGR (compound annual growth rate). In this process it is also helpful to figure out how much the market seems to be growing or shrinking. If you can estimate an annual growth rate, this is very helpful. If the growth rate is negative, it is important to understand why and where this money (that is decreasing the market size) is going.</p>
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Top-down analysis
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  Use market analysis reports and other secondary market research to determine the top-down estimation of your total addressable market:

                  Collect and analyze market reports. This could include demographic reports, trade group membership information, etc.
                  Define how many end-users are in the market.
                  Narrow down these end-users based on various criteria, resulting in an inverted pyramid.
                  Determine how many end-users are in your beachhead market (the bottom-most, smallest level of the pyramid, containing all end users who meet your End User Profile).
                  Know the budgets of the potential customers you have identified.
                  Identity how much they are spending today to accomplish what your product does.
                  Determine how much they pay for other products that solve this same problem or offer this same benefit.
                  Add up how much value or cost saving your product creates for them.
                  Calculate the top-down value of your TAM based on the above data.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_4">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_4"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_4"
                  >
                    Review key TAM drivers
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_4"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_4"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                    As a general rule, the TAM size should be between $5M and $100M. However, there are a few drivers that you should investigate, to make sure you've chosen a good TAM:

                    Estimate what your gross margins will be in this market. The TAM number represents revenue. So, a $10M TAM with 90% gross margins (e.g. software) is much more attractive than a $10M TAM with low margins (e.g. many energy markets).
                    Estimate how quickly you can dominate the market. If you can achieve dominant market share (i.e., you become the standard and have market penetration of greater than 25%) in a year, this makes a TAM much more attractive than one that has slow product turnover and will take 10 years to achieve significant market penetration.
                    Estimate what are the odds that you can achieve dominant market share, or will it be a battle with others where you will likely end up splitting the market? If the odds are high that you will dominate the market, because you have very strong critical IP or there are networking effects where the winner takes all, this is much more attractive than a market where you have neither of these.
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>

  );
};

export { StepContent4 };
